<template>
  <tab-item-template>
    <template #toolbar-items>
      <el-tooltip content="トリガとなるイベント実装作成" placement="bottom" v-if="isEditable" :open-delay="500">
        <v-btn icon :disabled="!processImplementation" @click="openCreateEventImplDialog">
          <v-icon>mdi-lightning-bolt</v-icon>
        </v-btn>
      </el-tooltip>
      <el-tooltip content="カレンダ実装作成" placement="bottom" v-if="isEditable" :open-delay="500">
        <v-btn icon :disabled="!processImplementation" @click="openCreateCalendarImplDialog">
          <v-icon>mdi-calendar</v-icon>
        </v-btn>
      </el-tooltip>
    </template>

    <el-table
        :data="triggerImplementations"
        row-key="id"
        :height="tableHeight"
        :default-sort="{prop: 'eventableName', order: 'ascending'}"
        border>
      <el-table-column
          label="名称"
          width="200"
          column-key="eventableName"
          sortable>
        <template #header>
          名称
          <el-tooltip content="カレンダの名称、またはイベントの名称／内部ID" placement="top" :open-delay="500">
            <v-icon small color="blue">mdi-information</v-icon>
          </el-tooltip>
        </template>
        <template #default="{row}">
          <template v-if="row.eventableType === 'EventImplementation'">
            <table-inline>
              <text-field-like
                  :value="row.eventableName"
                  :value-local-id="row.eventableLocalId"
                  placeholder="名称未設定">
                <template #prepend>
                  <el-tooltip content="トリガとなるイベント" :open-delay="500" placement="left">
                    <v-icon>mdi-lightning-bolt</v-icon>
                  </el-tooltip>
                </template>
              </text-field-like>
            </table-inline>
          </template>
          <template v-else-if="row.eventableType === 'CalendarImplementation'">
            <table-inline>
              <text-field-like
                  :value="row.eventableName"
                  :placeholder="'\u2E3B'">
                <template #prepend>
                  <el-tooltip content="カレンダ" :open-delay="500" placement="left">
                    <v-icon>mdi-calendar</v-icon>
                  </el-tooltip>
                </template>
              </text-field-like>
            </table-inline>
          </template>
        </template>
      </el-table-column>

      <el-table-column
          label="説明"
          prop="eventableDescription">
        <template #default="{row}">
          <table-inline>
            <text-field-like
              :value="row.eventableDescription"
              placeholder="未設定"
              multi-line>
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
        label="サービス">
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.serviceComponentModelName"
                :placeholder="'\u2E3B'">
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
        label="プロセス">
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.processComponentModelName"
                :placeholder="'\u2E3B'">
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
        width="60"
        align="right"
        fixed="right">
        <template #default="{row}">
          <icon-btns>
            <icon-btn
                v-if="isEditable"
                type="delete"
                @click="openDeleteDialog(row)">
            </icon-btn>
          </icon-btns>
        </template>
      </el-table-column>

      <template #empty>
        <v-row
            justify="center"
            class="pa-10"
            v-if="triggerImplementationsLoading">
          <v-progress-circular indeterminate/>
        </v-row>
        <v-row
            v-else-if="processImplementation == null"
            justify="center"
            class="pa-10">
          プロセス実装を選択してください
        </v-row>
      </template>
    </el-table>

    <!-- トリガとなるイベント実装作成ダイアログ -->
    <simple-dialog
      v-model="dialogCreateEvent.visible"
      title-text="トリガとなるイベント実装作成"
      @close="closeCreateEventImplDialog"
      @apply="createTriggerImplementation(dialogCreateEvent.eventImplementationIds, [])">
      <event-implementation-select-form
        v-model="dialogCreateEvent.eventImplementationIds"
        :process-implementation="processImplementation"
        :controller-id="serviceImplementation.controllerId"
        :initial-service-implementation-id="serviceImplementation.id"
        :existing-event-implementation-ids="
          triggerImplementations.filter(ti => ti.eventableType === 'EventImplementation').map(ti => ti.eventableId)
        ">
      </event-implementation-select-form>
    </simple-dialog>

    <!-- カレンダ実装作成ダイアログ -->
    <simple-dialog
        v-model="dialogCreateCalendar.visible"
        title-text="カレンダ実装作成"
        @close="closeCreateCalendarImplDialog"
        @apply="createTriggerImplementation([], dialogCreateCalendar.calendarIds)">
      <calendar-select-form
          v-model="dialogCreateCalendar.calendarIds"
          :process-implementation="processImplementation"
          :existing-calendar-ids="
            triggerImplementations.filter(ti => ti.eventableType === 'CalendarImplementation').map(ti => ti.calendarId)
          ">
      </calendar-select-form>
    </simple-dialog>

    <!-- 削除ダイアログ -->
    <simple-dialog
        v-model="dialogDelete.visible"
        title-text="トリガとなるイベント実装削除"
        title-color="delete"
        apply-text="削除"
        header-text="以下のトリガとなるイベント実装を削除します。本当によろしいですか？"
        @close="closeDeleteDialog"
        @apply="deleteTriggerImplementation(dialogDelete.triggerImplementation)">
      <trigger-implementation-delete-form
        v-model="dialogDelete.triggerImplementation">
      </trigger-implementation-delete-form>
    </simple-dialog>
  </tab-item-template>
</template>

<script>
import { mapState, mapMutations, mapGetters } from "vuex";
import PathInfoMixin from "@/router/PathInfoMixin";
import IoablePageMixin from "@/router/IoablePageMixin";
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import EventImplementationSelectForm from "@/components/pages/implementation/ServiceImplementationShow/EventImplementationSelectForm.vue";
import TriggerImplementationDeleteForm from "@/components/pages/implementation/ServiceImplementationShow/TriggerImplementationDeleteForm.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import TableInline from "@/components/atoms/TableInline.vue";
import IconBtns from "@/components/molecules/IconBtns";
import IconBtn from "@/components/molecules/IconBtn.vue";
import CalendarSelectForm from "@/components/pages/implementation/ServiceImplementationShow/CalendarSelectForm.vue";
import {doLayoutElTable} from "@/utils/DomUtil";

export default {
  name: 'TriggerImplementationTable',
  components: {
    CalendarSelectForm,
    IconBtn,
    IconBtns,
    TabItemTemplate,
    SimpleDialog,
    EventImplementationSelectForm,
    TriggerImplementationDeleteForm,
    TextFieldLike,
    TableInline
  },
  mixins: [
    PathInfoMixin,
    IoablePageMixin
  ],
  props: {
    processImplementation: {
      type: Object,
      default: null
    },
    serviceImplementation: {
      type: Object,
    },
    height: {
      type: Number,
    },
  },
  data() {
    return {
      dialogCreateEvent: {
        visible: false,
        eventImplementationIds: []
      },
      dialogCreateCalendar: {
        visible: false,
        calendarIds: []
      },
      dialogDelete: {
        visible: false,
        triggerImplementation: {}
      },
      triggerImplementations: [],
      triggerImplementationsLoading: false,
    }
  },
  computed: {
    ...mapGetters({
      hcsClient: 'hcs/client',
      hdsEntities: 'hds/entities'
    }),
    tableHeight() {
      return this.height ? Math.max(0, this.height - 48) : undefined;
    }
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    openCreateEventImplDialog() {
      this.dialogCreateEvent = {
        visible: true,
        eventImplementationIds: []
      };
    },
    closeCreateEventImplDialog() {
      this.dialogCreateEvent.visible = false
    },
    openCreateCalendarImplDialog() {
      this.dialogCreateCalendar = {
        visible: true,
        calendarIds: []
      };
    },
    closeCreateCalendarImplDialog() {
      this.dialogCreateCalendar.visible = false
    },
    openDeleteDialog(row) {
      this.dialogDelete = {
        visible: true,
        triggerImplementation: row
      }
    },
    closeDeleteDialog() {
      this.dialogDelete.visible = false;
    },
    createTriggerImplementation(eventImplementationIds, calendarIds) {
      this.showSnackbar({
        message: 'トリガとなるイベント実装を登録中...',
        timeout: false,
        loading: true
      });

      this.$store.dispatch('hcs/triggerImplementation/createMulti', {
        processImplementationId: this.processImplementation.id,
        eventImplementationIds,
        calendarIds
      })
        .then(tis => {
          this.$store.commit({
            type: 'showSnackbar',
            message: `トリガとなるイベント実装が登録されました`
          });

          tis.forEach(ti => this.triggerImplementations.push(ti));
          this.$emit('update-count', this.triggerImplementations.length);
        })
        .catch(() => {
          this.$store.commit({
            type: 'showSnackbar',
            color: 'failed',
            message: `トリガとなるイベント実装の登録に失敗しました`
          });
        })
    },
    deleteTriggerImplementation(triggerImplementation) {
      const pos = this.triggerImplementations.findIndex(ti => ti.id === triggerImplementation.id)
      if(pos !== -1) {
        this.triggerImplementations.splice(pos, 1);
        this.$emit('update-count', this.triggerImplementations.length);
      }

      this.showSnackbar({
        message: 'トリガとなるイベント実装を削除中...',
        timeout: false,
        loading: true
      });
      this.$store.dispatch('hcs/triggerImplementation/delete', triggerImplementation.id)
        .then(() => {
          this.$store.commit({
            type: 'showSnackbar',
            message: `トリガとなるイベント実装が削除されました`
          });
        })
        .catch(() => {
          this.$store.commit({
            type: 'showSnackbar',
            color: 'failed',
            message: `トリガとなるイベント実装の削除に失敗しました`
          });
        })
    }
  },
  watch: {
    processImplementation: {
      immediate: true,
      async handler(selectedProcessImplementation) {
        if(!selectedProcessImplementation) {
          return;
        }

        try {
          this.triggerImplementationsLoading = true;
          this.triggerImplementations = [];

          const {data: triggerImplementations} = await this.hcsClient.get('trigger_implementations', {
            params: {
              'q[process_implementation_id_eq]': selectedProcessImplementation.id
            }
          });

          this.triggerImplementations = triggerImplementations;
        } finally {
          this.triggerImplementationsLoading = false;
          doLayoutElTable(this.$el);
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.multipane.layout-v .multipane-resizer::after {
  content: "";
  position: relative;
  left: 4px;
  background-color: #ebeef5;
  display: block;
  width: 1px;
  height: 100%;
}
</style>
